import { isProduction } from '../../config';

/**
 * Every URL in the CRM section. Screens link through these rather than writing
 * paths by hand, so a route renamed here cannot leave a dead link elsewhere.
 * The route table itself is CrmRoutes.tsx; the sidebar entries are Sidebar.tsx.
 */
export const CRM_ROOT = '/crm';

export const CRM_PATHS = {
  // People
  contacts: `${CRM_ROOT}/contacts`,
  lists: `${CRM_ROOT}/lists`,
  tags: `${CRM_ROOT}/tags`,
  batchUpdates: `${CRM_ROOT}/batch-updates`,
  matchReview: `${CRM_ROOT}/match-review`,
  import: `${CRM_ROOT}/import`,
  // Outreach
  callQueue: `${CRM_ROOT}/call-queue`,
  followUps: `${CRM_ROOT}/follow-ups`,
  callLog: `${CRM_ROOT}/calls`,
  email: `${CRM_ROOT}/email`,
  workflows: `${CRM_ROOT}/workflows`,
} as const;

export const contactPath = (contactId: number) => `${CRM_PATHS.contacts}/${contactId}`;
/** A list's or saved filter's own page (card 20). */
export const listPath = (listId: number) => `${CRM_PATHS.lists}/${listId}`;
/** The contact list showing everyone on a list, or everyone a saved filter matches (card 20). */
export const listPeoplePath = (listId: number) => `${CRM_PATHS.contacts}?lists=${listId}`;
/** The call log showing one person's calls (card 21.6). */
export const personCallsPath = (contactId: number) => `${CRM_PATHS.callLog}?person=${contactId}`;
export const emailCampaignPath = (campaignId: number) => `${CRM_PATHS.email}/${campaignId}`;
export const newEmailCampaignPath = `${CRM_PATHS.email}/new`;
/** A new campaign already going to a list or saved filter. */
export const emailListPath = (listId: number) => `${newEmailCampaignPath}?to=${listId}`;
export const workflowPath = (workflowId: number) => `${CRM_PATHS.workflows}/${workflowId}`;
export const newWorkflowPath = `${CRM_PATHS.workflows}/new`;

/**
 * Screens whose body is still a placeholder (CrmPendingScreen). The CRM itself
 * is in every build; in production these screens' sidebar entries and routes
 * are left out, so an admin never meets an empty frame. **The card that builds
 * a screen removes its path here.** A detail page goes with its list: contacts
 * covers /crm/contacts/:id, workflows covers /crm/workflows/new and /:id.
 *
 * Contacts is live: its list is built (card 18), and its detail page (card 19).
 * Lists and Tags are live: their managers are built on card 17.5's rename and
 * delete, taken ahead of that card (ilbp-backend CRM_TAG_AND_LIST_MANAGER.md).
 * Batch updates are live: built in card 20, with a list's own page.
 * The call queue is live: built in card 21, with the log-a-call form.
 * The call log is live: built in card 21.6.
 * Follow-ups are live: built in card 22. Match review is live: built in card 23.
 * Email and Workflows are live: built on card 17.5's API (plan 4.4 and 4.5).
 */
export const CRM_PENDING: ReadonlySet<string> = new Set<string>([
  CRM_PATHS.import, // plan 4.6 #5, no card yet
]);

/** Whether a CRM screen is reachable: always outside production, once built in it. */
export const isCrmScreenLive = (
  path: string,
  pending: ReadonlySet<string> = CRM_PENDING,
  production: boolean = isProduction,
) => !production || !pending.has(path);

/** GraphQL `Int` is 32-bit signed: a larger id would fail validation, not find nobody. */
const MAX_GRAPHQL_INT = 2147483647;

/**
 * The record id in a path such as `/crm/contacts/:contactId`, or null when the
 * segment is not one. Strict on purpose: `parseInt('12abc')` is 12, and a typo
 * would otherwise open somebody else's record.
 */
export const parseId = (param: string | undefined): number | null => {
  if (!param || !/^[1-9]\d*$/.test(param)) return null;

  const id = Number(param);

  return id <= MAX_GRAPHQL_INT ? id : null;
};

/**
 * Router state a CRM screen passes when it opens a contact, so the detail page's
 * way back returns to the list as it was left (filters, search, page) instead
 * of its first page:
 *
 *   navigate(contactPath(id), { state: crmReturnState(location) })
 */
export interface CrmReturnState {
  from: string;
  fromLabel: string;
}

/** The screens a contact can be opened from. */
const LABELS: Record<string, string> = {
  [CRM_PATHS.contacts]: 'Contacts',
  [CRM_PATHS.lists]: 'Lists',
  [CRM_PATHS.tags]: 'Tags',
  [CRM_PATHS.matchReview]: 'Match review',
  [CRM_PATHS.callQueue]: 'Call queue',
  [CRM_PATHS.followUps]: 'Follow-ups',
  [CRM_PATHS.callLog]: 'Call log',
  [CRM_PATHS.batchUpdates]: 'Batch updates',
};

/** A list's own page is an origin too: "back" returns to it, named "List". */
const LIST_PAGE = new RegExp(`^${CRM_PATHS.lists}/[1-9]\\d*$`);

const labelOf = (pathname: string): string | undefined => LABELS[pathname] ?? (LIST_PAGE.test(pathname) ? 'List' : undefined);

export const crmReturnState = (location: { pathname: string; search: string }): CrmReturnState => ({
  from: `${location.pathname}${location.search}`,
  fromLabel: labelOf(location.pathname) ?? 'Contacts',
});

/**
 * Where "back" goes from a contact. Only another CRM list counts: router state
 * survives a reload and can be anything, so an unknown origin falls back to the
 * contact list rather than sending the admin somewhere unexpected.
 */
export const returnTarget = (state: unknown): CrmReturnState => {
  const candidate = state as Partial<CrmReturnState> | null | undefined;
  const from = typeof candidate?.from === 'string' ? candidate.from : '';
  const pathname = from.split(/[?#]/)[0];

  const label = labelOf(pathname);
  if (label) return { from, fromLabel: label };

  return { from: CRM_PATHS.contacts, fromLabel: LABELS[CRM_PATHS.contacts] };
};
